import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Receipt, FileText } from 'lucide-react';
import { toast } from 'sonner';
import StaffLayout from '../../layouts/StaffLayout';
import { operationalCostsApi } from '../../services/api';
import { formatCurrency, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const COST_CATEGORIES = ['Bahan Baku', 'Listrik & Air', 'Gaji Karyawan', 'Sewa Tempat', 'Transportasi', 'Marketing', 'Peralatan', 'Umum'];

function AddCostModal({ onClose, onSuccess, editData }) {
  const [form, setForm] = useState({
    name: editData?.name || '',
    amount: editData?.amount || '',
    category: editData?.category || 'Umum',
    date: editData?.date || new Date().toISOString().split('T')[0],
    notes: editData?.notes || '',
  });
  const mutation = useMutation({
    mutationFn: () => editData
      ? operationalCostsApi.update(editData.id, form)
      : operationalCostsApi.create({ ...form, amount: parseFloat(form.amount) }),
    onSuccess: () => { toast.success(editData ? 'Biaya diperbarui!' : 'Biaya ditambahkan!'); onSuccess(); },
    onError: err => toast.error(formatApiError(err)),
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 font-manrope">{editData ? 'Edit Biaya' : 'Tambah Biaya Operasional'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500 text-lg leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Nama Biaya *</label>
            <input value={form.name} onChange={set('name')} placeholder="Sabun detergen, tagihan listrik..." autoFocus
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Jumlah *</label>
              <input type="number" value={form.amount} onChange={set('amount')} placeholder="0"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Kategori</label>
              <select value={form.category} onChange={set('category')}
                className="w-full py-3 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none">
                {COST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Tanggal</label>
              <input type="date" value={form.date} onChange={set('date')}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Catatan</label>
              <input value={form.notes} onChange={set('notes')} placeholder="Opsional"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-11 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors">Batal</button>
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name || !form.amount}
              className="flex-1 h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
              {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : 'Simpan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StaffCostsPage() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editData, setEditData] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 7) + '-01';

  const { data: costs = [], isLoading, refetch } = useQuery({
    queryKey: ['staff-costs'],
    queryFn: () => operationalCostsApi.list(monthStart, today).then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: id => operationalCostsApi.remove(id),
    onSuccess: () => { refetch(); toast.success('Biaya dihapus!'); },
    onError: err => toast.error(formatApiError(err)),
  });

  const totalMonth = costs.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <StaffLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Biaya Operasional', 'Operational Costs')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Catat biaya harian toko', 'Record daily store expenses')}</p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-cost-button"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={16} />{t('Tambah Biaya', 'Add Cost')}
          </button>
        </div>

        {/* Summary Card */}
        <div className="bg-[#0f6c44] rounded-2xl p-5 text-white shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
              <Receipt size={16} className="text-white" />
            </div>
            <p className="text-sm text-white/80">{t('Total Biaya Bulan Ini', 'Total This Month')}</p>
          </div>
          <p className="text-3xl font-extrabold font-manrope">{formatCurrency(totalMonth)}</p>
          <p className="text-xs text-white/60 mt-1">{costs.length} {t('item tercatat', 'items recorded')}</p>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 font-manrope text-sm">{t('Daftar Biaya Bulan Ini', 'This Month\'s Expenses')}</h3>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><div className="w-7 h-7 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" /></div>
          ) : costs.length === 0 ? (
            <div className="text-center py-12">
              <Receipt size={32} className="mx-auto text-slate-200 mb-3" />
              <p className="text-slate-400 text-sm">{t('Belum ada biaya bulan ini', 'No expenses this month')}</p>
              <button onClick={() => setShowAdd(true)} className="mt-3 text-[#0f6c44] text-sm font-semibold hover:underline">
                {t('+ Tambah sekarang', '+ Add now')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {costs.map(c => (
                <div key={c.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.date} · <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-xs">{c.category}</span></p>
                    {c.notes && <p className="text-xs text-slate-400 mt-0.5 italic">{c.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    <span className="font-bold text-orange-600 font-manrope">{formatCurrency(c.amount)}</span>
                    <button onClick={() => { setEditData(c); setShowAdd(true); }}
                      className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title="Edit">
                      <FileText size={14} className="text-slate-400" />
                    </button>
                    <button onClick={() => deleteMutation.mutate(c.id)}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Hapus">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex justify-between px-5 py-4 bg-slate-50/70">
                <span className="font-bold text-slate-700 text-sm">Total</span>
                <span className="font-extrabold text-orange-600 font-manrope">{formatCurrency(totalMonth)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {(showAdd || editData) && (
        <AddCostModal
          editData={editData}
          onClose={() => { setShowAdd(false); setEditData(null); }}
          onSuccess={() => { setShowAdd(false); setEditData(null); refetch(); }}
        />
      )}
    </StaffLayout>
  );
}
